import React, { useState, useCallback, useEffect } from "react";
import styles from "./ResizableSplitter.module.css";

interface ResizableSplitterProps {
    leftPanel: React.ReactNode;
    rightPanel: React.ReactNode;
    defaultRightWidth?: number;
    minRightWidth?: number;
    maxRightWidth?: number;
    className?: string;
}

const ResizableSplitter: React.FC<ResizableSplitterProps> = ({
    leftPanel,
    rightPanel,
    defaultRightWidth = 450,
    minRightWidth = 350,
    maxRightWidth = 800,
    className = ""
}) => {
    const [rightWidth, setRightWidth] = useState(defaultRightWidth);
    const [isResizing, setIsResizing] = useState(false);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        setIsResizing(true);
    }, []);

    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (!isResizing) return;

        const sidebarWidth = window.innerWidth <= 768 ? 60 : 200; // Account for sidebar
        const containerWidth = window.innerWidth - sidebarWidth;
        const newRightWidth = containerWidth - (e.clientX - sidebarWidth);

        // Ensure left panel has at least 400px width
        const minLeftWidth = 400;
        const maxAllowedRightWidth = Math.min(maxRightWidth, containerWidth - minLeftWidth);

        if (newRightWidth >= minRightWidth && newRightWidth <= maxAllowedRightWidth) {
            setRightWidth(newRightWidth);
        }
    }, [isResizing, minRightWidth, maxRightWidth]);

    const handleMouseUp = useCallback(() => {
        setIsResizing(false);
    }, []);

    useEffect(() => {
        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'ew-resize';
            document.body.style.userSelect = 'none';
            
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
            };
        }
    }, [isResizing, handleMouseMove, handleMouseUp]);

    return (
        <div className={`${styles.container} ${className}`}>
            <div className={styles.leftPanel} style={{ flex: 1, marginRight: rightWidth + 8 }}>
                {leftPanel}
            </div>
            
            <div 
                className={`${styles.splitter} ${isResizing ? styles.resizing : ''}`}
                onMouseDown={handleMouseDown}
                style={{ right: rightWidth }}
            >
                <div className={styles.splitterHandle} />
            </div>
            
            <div className={styles.rightPanel} style={{ width: rightWidth }}>
                {rightPanel}
            </div>
        </div>
    );
};

export default ResizableSplitter;